import { Organization } from "fhir/r5";

// Types
export type OrganizationNode = Organization & {
  children: OrganizationNode[];
};

export type OrganizationFilterResult = {
  nodes: OrganizationNode[];
  expandedKeys: Set<string>;
  matchKeys: Set<string>;
  matches: number;
};

export type OrganizationIndex = {
  nodeByKey: Map<string, Organization>;
  parentByKey: Map<string, string>;
};

// Normalization
export function normalizeKey(value?: string | null): string {
  return (value ?? "").trim();
}

export function normalizeSearchValue(value?: string | null): string {
  return (value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Lookup helpers
export function getOrganizationKey(organization: Organization): string {
  return (
    normalizeKey(
      organization.identifier?.find((identifier) => identifier.use === "official")
        ?.value
    ) || normalizeKey(organization.id) || normalizeKey(organization.name)
  );
}

export function getParentKey(organization: Organization): string {
  return (
    normalizeKey(organization.partOf?.identifier?.value) ||
    normalizeKey(organization.partOf?.reference?.split("/").pop()) ||
    normalizeKey(organization.partOf?.display)
  );
}

export function getDisplayName(organization: Organization): string {
  return (
    organization.name?.trim() || organization.alias?.[0] || organization.id || "N/A"
  );
}

export function getSearchTerms(organization: Organization): string[] {
  return [
    organization.id,
    organization.name,
    ...(organization.identifier?.map((identifier) => identifier.value) ?? []),
  ]
    .filter(Boolean)
    .map((value) => normalizeSearchValue(value));
}

export function matchesOrganization(
  organization: Organization,
  searchTerm: string
): boolean {
  if (!searchTerm) {
    return true;
  }

  return getSearchTerms(organization).some((value) => value.includes(searchTerm));
}

// Tree construction
export function sortOrganizationNodes(nodes: OrganizationNode[]): OrganizationNode[] {
  return [...nodes]
    .sort((left, right) => getDisplayName(left).localeCompare(getDisplayName(right)))
    .map((node) => ({
      ...node,
      children: sortOrganizationNodes(node.children),
    }));
}

export function buildOrganizationTree(
  organizations: Organization[]
): OrganizationNode[] {
  const nodeByKey = new Map<string, OrganizationNode>();
  const parentByKey = new Map<string, string>();
  const childKeys = new Set<string>();

  organizations.forEach((organization) => {
    const key = getOrganizationKey(organization);
    if (!key) {
      return;
    }

    nodeByKey.set(key, { ...organization, children: [] });
    const parentKey = getParentKey(organization);
    if (parentKey) {
      parentByKey.set(key, parentKey);
    }
  });

  parentByKey.forEach((parentKey, childKey) => {
    const parentNode = nodeByKey.get(parentKey);
    const childNode = nodeByKey.get(childKey);

    if (parentNode && childNode && parentKey !== childKey) {
      parentNode.children.push(childNode);
      childKeys.add(childKey);
    }
  });

  return sortOrganizationNodes(
    Array.from(nodeByKey.entries())
      .filter(([key]) => !childKeys.has(key))
      .map(([, node]) => node)
  );
}

// Tree indexing
export function buildOrganizationIndex(
  organizations: Organization[]
): OrganizationIndex {
  const nodeByKey = new Map<string, Organization>();
  const parentByKey = new Map<string, string>();

  organizations.forEach((organization) => {
    const key = getOrganizationKey(organization);
    if (!key) {
      return;
    }

    nodeByKey.set(key, organization);

    const parentKey = getParentKey(organization);
    if (parentKey) {
      parentByKey.set(key, parentKey);
    }
  });

  return { nodeByKey, parentByKey };
}

// Metrics
export function countOrganizationNodes(nodes: OrganizationNode[]): number {
  return nodes.reduce((total, node) => total + 1 + countOrganizationNodes(node.children), 0);
}

export function countOrganizationLeaves(nodes: OrganizationNode[]): number {
  return nodes.reduce(
    (total, node) =>
      total + (node.children.length === 0 ? 1 : countOrganizationLeaves(node.children)),
    0
  );
}

// Path and search helpers
export function collectOrganizationKeys(nodes: OrganizationNode[]): string[] {
  const keys: string[] = [];

  nodes.forEach((node) => {
    const key = getOrganizationKey(node) || node.id || getDisplayName(node);
    keys.push(key);
    keys.push(...collectOrganizationKeys(node.children));
  });

  return keys;
}

// Path resolution
export function getOrganizationPath(
  organization: Organization | null | undefined,
  organizations: Organization[]
): Organization[] {
  if (!organization) {
    return [];
  }

  const index = buildOrganizationIndex(organizations);
  const currentKey =
    getOrganizationKey(organization) || organization.id || organization.name || "";

  if (!currentKey) {
    return [organization];
  }

  const path: Organization[] = [];
  const visited = new Set<string>();
  let cursorKey: string | undefined = currentKey;

  while (cursorKey && !visited.has(cursorKey)) {
    visited.add(cursorKey);
    const current = index.nodeByKey.get(cursorKey);
    if (!current) {
      break;
    }

    path.push(current);
    cursorKey = index.parentByKey.get(cursorKey);
  }

  if (path.length === 0) {
    path.push(organization);
  }

  return path.reverse();
}

// Filtering
export function countOrganizationsByLevel(nodes: OrganizationNode[]): number[] {
  const counts: number[] = [];

  const visit = (items: OrganizationNode[], depth: number) => {
    items.forEach((node) => {
      counts[depth] = (counts[depth] ?? 0) + 1;
      visit(node.children, depth + 1);
    });
  };

  visit(nodes, 0);

  return counts;
}

export function filterOrganizationTree(
  nodes: OrganizationNode[],
  searchTerm: string
): OrganizationFilterResult {
  if (!searchTerm) {
    return {
      nodes,
      expandedKeys: new Set<string>(),
      matchKeys: new Set<string>(),
      matches: countOrganizationNodes(nodes),
    };
  }

  const result: OrganizationFilterResult = {
    nodes: [],
    expandedKeys: new Set<string>(),
    matchKeys: new Set<string>(),
    matches: 0,
  };

  nodes.forEach((node) => {
    const key = getOrganizationKey(node) || node.id || getDisplayName(node);
    const childResult = filterOrganizationTree(node.children, searchTerm);
    const selfMatches = matchesOrganization(node, searchTerm);

    if (selfMatches || childResult.nodes.length > 0) {
      result.nodes.push({
        ...node,
        children: selfMatches ? node.children : childResult.nodes,
      });
      result.matches += selfMatches ? 1 : 0;
      result.matches += childResult.matches;
      if (selfMatches) {
        result.matchKeys.add(key);
      }
      childResult.matchKeys.forEach((childKey) => result.matchKeys.add(childKey));

      if (!selfMatches && childResult.nodes.length > 0) {
        result.expandedKeys.add(key);
      }
      childResult.expandedKeys.forEach((childKey) =>
        result.expandedKeys.add(childKey)
      );
    }
  });

  return result;
}
